import type { Translation } from '../types';

export const zhHans: Translation = {
  meta: { name: '简体中文', english: 'Chinese (Simplified)', tag: 'zh-Hans', dir: 'ltr' },

  common: {
    menu: "菜单",
    cancel: '取消',
    done: '完成',
    join: '加入',
    leave: '离开',
    refresh: '刷新',
    save: '保存',
    you: '你',
    host: '房主',
    ai: 'AI',
    ready: '已准备',
    notReady: '未准备',
  },

  conn: {
    live: '已连接',
    connecting: '连接中',
    offline: '已断开',
    connectingFull: '连接中…',
    reconnectBanner: '连接已断开，正在重连。你的座位会保留 3 分钟。',
  },

  nickname: {
    pickTitle: '取个名字',
    changeTitle: '修改名字',
    subtitle: '其他玩家会看到这个名字。',
    placeholder: '例如：万能+4',
    label: '昵称',
    letsPlay: '开始游戏',
  },

  lobby: {
    heroTitle: '和朋友一起玩 UNO',
    heroSubtitle: '无需注册，分享链接即可开局。',
    createRoom: '创建房间',
    roomCode: '房间号',
    publicRooms: '公开房间',
    empty: '还没有公开房间，来创建第一个吧。',
    cardMeta: '房主 {host} · 每回合 {seconds} 秒',
    stacking: '可叠加',
    badgeOpen: '可加入',
    badgeFull: '已满',
    badgeLive: '游戏中',
    lockedTitle: '房间 {id} 已加密',
    password: '密码',
    passwordLabel: '房间密码',
  },

  create: {
    title: '新建房间',
    roomName: '房间名称',
    roomNamePlaceholder: '周五之夜 UNO',
    maxPlayers: '最大人数',
    playersOption: { other: '{count} 人' },
    passwordOptional: '密码（可选）',
    passwordPlaceholder: '留空则任何人可加入',
    listPublic: '显示在公开房间列表中',
    houseRules: '自定义规则',
    submit: '创建',
  },

  rules: {
    turnTimer: '回合时限',
    turnTimerHint: '超时后，机器人会替你随机打出一张合法的牌。',
    startingHand: '起始手牌',
    unoPenalty: 'UNO 罚牌',
    stackDrawTwo: '+2 叠加',
    stackDrawTwoHint: '可用 +2 回应 +2，无需摸牌',
    stackDrawFour: '+4 叠加',
    stackDrawFourHint: '可用 +4 回应 +4',
    challengeWildFour: '+4 质疑',
    challengeWildFourHint: '下一位玩家可以质疑不合法的 +4',
    strictWildFour: '严格 +4',
    strictWildFourHint: '仅在手中没有当前颜色的牌时才能打出',
    playDrawn: '摸到即可打出',
    playDrawnHint: '摸到的牌可以立刻打出',
    drawUntilPlayable: '摸到能出为止',
    drawUntilPlayableHint: '一直摸牌直到摸到可以打出的牌',
    hostOnly: '只有房主可以修改',
  },

  room: {
    copyLink: '复制邀请链接',
    copied: '✓ 已复制',
    roomLabel: '房间',
    locked: '已加密',
    public: '公开',
    unlisted: '不公开',
    readyStatus: '{total} 人中已有 {ready} 人准备',
    botsJoining: { other: '{count} 个机器人将加入' },
    startsWhenReady: '所有人准备就绪后立即开始。',
    needMorePlayers: '至少需要 {min} 人才能开始，请邀请好友或添加机器人。',
    playersTitle: '玩家',
    botsTitle: 'AI 机器人',
    botsHostOnly: '只有房主可以修改',
    noBots: '没有机器人',
    botsWillJoin: { other: '{count} 个机器人将加入' },
    botsHint: '游戏开始时入座。当前最多可容纳 {max} 个。',
    botsRoomFull: '房间已满，无法添加机器人。',
    soloCta: '一个人玩？添加机器人即可开始',
    addBot: '添加机器人',
    removeBot: '减少机器人',
    removePlayer: '移除 {name}',
    botPlaceholder: '机器人 {n}',
    defaultRoomName: '{name} 的房间',
    imReady: '我准备好了',
    cancelReady: '取消准备',
    offlineTag: '离线',
  },

  game: {
    yourTurn: '轮到你了',
    yourTurnStack: '轮到你了 — 叠加或摸 {count} 张',
    othersTurn: '轮到 {name}',
    waiting: '等待中…',
    gameOver: '游戏结束',
    challengeWindow: '万能 +4 — 质疑时间',
    drawPileLeft: '剩余 {count} 张',
    discard: '弃牌堆',
    draw: '摸牌',
    takeCards: '摸 {count} 张',
    pass: '过牌',
    uno: 'UNO！',
    judge: '质疑',
    cardCount: { other: '{count} 张牌' },
    clockwise: '顺时针',
    counterClockwise: '逆时针',
    activeColour: '当前颜色',
    noCards: '手牌已出完 — 漂亮。',
    oneCard: '只剩一张！',
    unoTag: 'UNO',
    forfeitedTag: '已退出',
    offlineFor: '离线 {seconds} 秒',
    placeTag: '第 {place} 名',
    pickColour: '选择颜色',
    challengeTitle: '{name} 打出了万能 +4，要质疑吗？',
    challengeExplain: '若对方虚张声势，则由对方摸 {bluff} 张；否则你摸 {fail} 张。',
    challengeBtn: '质疑',
    acceptBtn: '摸 {count} 张',
    youWin: '🎉 你赢了！',
    standingsTitle: '游戏结束',
    points: '{points} 分',
    forfeited: '已退出',
    backToLobby: '返回大厅',
    playAgain: '再来一局',
    waitingOthers: '等待其他玩家…',
    faceDownCard: '背面朝上的牌',
    sortByColour: "按颜色排序",
    sortAsDealt: "保持发牌顺序",
    drawACard: '摸一张牌',
    drawNCards: '摸 {count} 张牌',
  },

  events: {
    someone: '有人',
    unoCalledYou: '你喊了 UNO！',
    unoCalledOther: '{name} 喊了 UNO！',
    judged: '{accuser} 抓到了 {target} — 罚 {count} 张',
    judgedYou: '{accuser} 抓到了你 — 罚 {count} 张',
    challengeBluff: '虚张声势被识破 — {name} 摸 {count} 张',
    challengeFailed: '质疑失败 — {name} 摸 {count} 张',
    forfeited: '{name} 已退出。',
    afkYou: '你超时了 — 已自动出牌。',
    afkOther: '{name} 超时了 — 已自动出牌。',
    hostChanged: '{name} 成为新房主。',
    reshuffled: '摸牌堆已重新洗牌。',
    youWin: '你赢了！',
    winner: '{name} 获胜！',
    gameOver: '游戏结束。',
  },

  errors: {
    bad_request: '出了点问题。',
    no_session: '会话已过期，正在重连。',
    room_not_found: '该房间不存在。',
    room_full: '该房间已满。',
    wrong_password: '密码错误。',
    already_in_room: '请先离开当前房间。',
    not_in_room: '你不在任何房间中。',
    not_host: '只有房主可以操作。',
    game_in_progress: '该游戏已经开始。',
    not_enough_players: '人数不足。',
    stale_action: '其他玩家先一步行动，你的操作已被撤销。',
    illegal_action: '不允许这个操作。',
    rate_limited: '操作太快了。',
    nickname_invalid: '这个昵称不可用。',
  },

  theme: {
    title: '卡牌主题',
    description: '缺少的图片会自动回退到内置的 CSS 卡面。',
    builtIn: '内置 — 无需任何图片',
    pngHint: '把 300×450 的 PNG 放进 {path}，然后在主题索引中注册。',
  },

  language: {
    title: '语言',
    description: '立即生效，并会记住在本设备上的选择。',
  },

  cards: {
    red: "红",
    yellow: "黄",
    green: "绿",
    blue: "蓝",
    wild: "万能",
    skip: "跳过",
    reverse: "反转",
    draw2: "+2",
    wildCard: "万能",
    wild4: "+4",
  },

  log: {
    title: "对局记录",
    empty: "暂无记录。",
    close: "隐藏记录",
    timestamps: "显示时间",
    chatPlaceholder: "说点什么…",
    send: "发送",
    played: "{name} 打出了 {card}",
    drew: "{name} 摸了 {count} 张",
    skipped: "{name} 被跳过",
    reversed: "方向已反转",
    joined: "{name} 加入了",
    left: "{name} 离开了",
    started: "游戏开始",
  },

  profile: {
    avatar: "头像",
    avatarHint: "选一个头像，或者保留首字母。",
  },

  leave: {
    title: "要离开牌局吗？",
    body: "本局还在进行中。离开将视为退出，你的手牌会放回牌堆。",
    stay: "继续游戏",
    confirm: "仍要离开",
    unload: "你正在牌局中。",
  },
};
